import React, { useCallback, useMemo } from 'react';
import { SpaceBetween } from '@amzn/awsui-components-core';
import { AiNarrative } from 'src/components/fintech-ops/shared/narrative/AiNarrative';
import { EmbeddedDashboard } from './EmbeddedDashboard';
import { useDashboardNarrativeData } from './hooks/useDashboardNarrativeData';
import { DashboardNarrativeService } from './narrative/DashboardNarrativeService';

interface DashboardTabContentProps {
  /** Registry id, used to key the narrative cache stably per dashboard. */
  dashboardId: string;
  /** CloudWatch dashboard name — drives both the embed and the metric extraction. */
  dashboardName: string;
  /** Human-readable name shown in the narrative context. */
  displayName: string;
}

/**
 * One KPI Metrics tab: an AI summary of the dashboard's current-month singleValue
 * metrics above the embedded CloudWatch dashboard itself. Cloudscape `Tabs` mounts only
 * the active tab's content, so the metric extraction runs only for the visible tab.
 *
 * The narrative renders while its data loads (spinner) and once resolved with at least
 * one widget; if extraction fails or the dashboard has no singleValue widgets, only the
 * embedded dashboard is shown.
 */
export const DashboardTabContent: React.FC<DashboardTabContentProps> = ({ dashboardId, dashboardName, displayName }) => {
  const { widgets, windowLabel, loading, error } = useDashboardNarrativeData(dashboardName);

  // Context string: identifies the narrative and invalidates its cache if it changes.
  const filters = useMemo(() => JSON.stringify({ dashboardName: displayName, month: windowLabel }), [displayName, windowLabel]);

  const generate = useCallback(
    () => DashboardNarrativeService.generateDashboardSummary({ dashboardName: displayName, month: windowLabel, widgets }),
    [displayName, windowLabel, widgets]
  );

  // Show the narrative box while data loads (its own spinner) or once we have widgets to
  // summarize. On error or an empty widget set, skip it and show just the dashboard.
  const showNarrative = !error && (loading || widgets.length > 0);

  return (
    <SpaceBetween size="m">
      {showNarrative && (
        <AiNarrative narrativeKey={`kpi-${dashboardId}`} filters={filters} generate={generate} ready={!loading} title="AI Dashboard Summary" />
      )}
      <EmbeddedDashboard dashboardName={dashboardName} />
    </SpaceBetween>
  );
};
